import i18next from 'i18next';
import type { Languages } from '../types/common';
import authTranslationsEn from '../components/hd-auth/assets/i18n/en.json';
import authTranslationsEs from '../components/hd-auth/assets/i18n/es.json';
import authTranslationsPt from '../components/hd-auth/assets/i18n/pt.json';

const TRANSLATIONS_MAP = {
  'hd-auth': {
    en: authTranslationsEn,
    es: authTranslationsEs,
    pt: authTranslationsPt,
  }
}

class I18nService {
  private static instance: I18nService;
  public t: typeof i18next.t;
  private initialized = false;
  private currentLanguage: Languages = 'en';

  private constructor() {
    this.t = i18next.t.bind(i18next);
  }

  public static getInstance(): I18nService {
    if (!I18nService.instance) {
      I18nService.instance = new I18nService();
    }
    return I18nService.instance;
  }

  async init(language: Languages = 'en') {
    if (this.initialized && this.currentLanguage === language) return;

    this.currentLanguage = language;

    await i18next.init({
      lng: language,
      fallbackLng: 'en',
      supportedLngs: ['en', 'es', 'pt', 'zh'],
      resources: {
        en: {
          'hd-auth': authTranslationsEn
        },
        es: {
          'hd-auth': authTranslationsEs
        },
        pt: {
          'hd-auth': authTranslationsPt
        }
      },
      ns: ['hd-auth'],
      defaultNS: 'hd-auth',
      interpolation: {
        escapeValue: false,
      },
    });

    this.t = i18next.t;
    this.initialized = true;
  }

  async loadComponentTranslations(componentName: string, lang: Languages) {
    try {
      // Use the static translations map
      const translations = TRANSLATIONS_MAP[componentName]?.[lang];

      if (translations) {
        i18next.addResourceBundle(lang, componentName, translations, true, true);
        return true;
      } else {
        // Fallback to English
        const fallbackTranslations = TRANSLATIONS_MAP[componentName]?.['en'];
        if (fallbackTranslations) {
          i18next.addResourceBundle(lang, componentName, fallbackTranslations, true, true);
          console.warn(`Using English fallback for ${componentName} in ${lang}`);
          return true;
        }
        return false;
      }
    } catch (error) {
      console.warn(`Failed to load translations for ${componentName} in ${lang}:`, error);
      return false;
    }
  }

  // Helper to get a namespaced translation
  get(key: string, namespace: string = 'hd-auth') {
    return this.t(key, { ns: namespace });
  }

  getCurrentLanguage(): Languages {
    return this.currentLanguage;
  }
}

export const i18n = I18nService.getInstance();